import React, { useState } from 'react';
import {
    Card,
    Tabs,
    Table,
    Button,
    Modal,
    Form,
    Input,
    DatePicker,
    Typography,
    Space,
    Tag,
    message,
    Spin,
    Alert,
    Checkbox,
    Statistic,
    Row,
    Col,
    Tooltip,
    Divider,
} from 'antd';
import {
    FileTextOutlined,
    SafetyOutlined,
    PlusOutlined,
    CheckCircleOutlined,
    EyeOutlined,
    RocketOutlined,
    HistoryOutlined,
    UserOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import dayjs from 'dayjs';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetAdminLegalDocumentsQuery,
    useCreateLegalDocumentMutation,
    useActivateLegalDocumentMutation,
    useGetLegalDocumentStatsQuery,
} from '../../services/nestjs-base-api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface LegalDocument {
    id: number;
    documentType: string;
    version: string;
    title: string;
    content: string;
    summary: string | null;
    isCurrent: boolean;
    effectiveDate: string;
    createdAt: string;
    createdBy?: { id: number; email: string; firstName: string; lastName: string };
}

export const AdminLegalDocumentsPage: React.FC = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('privacy_policy');
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [previewModalVisible, setPreviewModalVisible] = useState(false);
    const [activateModalVisible, setActivateModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<LegalDocument | null>(null);
    const [documentTypeToCreate, setDocumentTypeToCreate] = useState<string>('privacy_policy');
    const [form] = Form.useForm();

    const {
        data: documents,
        isLoading,
        refetch,
    } = useGetAdminLegalDocumentsQuery({
        jwtToken: user?.jwtToken || '',
    });

    const [createDocument, { isLoading: isCreating }] = useCreateLegalDocumentMutation();
    const [activateDocument, { isLoading: isActivating }] = useActivateLegalDocumentMutation();

    // Stats for selected document
    const { data: documentStats } = useGetLegalDocumentStatsQuery(
        { jwtToken: user?.jwtToken || '', id: selectedDocument?.id || 0 },
        { skip: !selectedDocument?.id || !activateModalVisible }
    );

    const handleCreateDocument = async (values: any) => {
        try {
            await createDocument({
                jwtToken: user?.jwtToken || '',
                documentType: documentTypeToCreate,
                version: values.version,
                title: values.title,
                content: values.content,
                summary: values.summary,
                effectiveDate: values.effectiveDate.toISOString(),
                activateImmediately: values.activateImmediately,
            }).unwrap();

            message.success('Legal document created successfully');
            setCreateModalVisible(false);
            form.resetFields();
            refetch();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to create document');
        }
    };

    const handleActivateDocument = async () => {
        if (!selectedDocument) return;

        try {
            await activateDocument({
                jwtToken: user?.jwtToken || '',
                id: selectedDocument.id,
                requireReAcceptance: false,
            }).unwrap();

            message.success(`${selectedDocument.title} v${selectedDocument.version} is now active`);
            setActivateModalVisible(false);
            setSelectedDocument(null);
            refetch();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to activate document');
        }
    };

    const openCreateModal = (documentType: string) => {
        setDocumentTypeToCreate(documentType);
        form.resetFields();
        form.setFieldsValue({
            effectiveDate: dayjs(),
            activateImmediately: false,
        });
        setCreateModalVisible(true);
    };

    const openPreviewModal = (doc: LegalDocument) => {
        setSelectedDocument(doc);
        setPreviewModalVisible(true);
    };

    const openActivateModal = (doc: LegalDocument) => {
        setSelectedDocument(doc);
        setActivateModalVisible(true);
    };

    const columns = [
        {
            title: 'Version',
            dataIndex: 'version',
            key: 'version',
            width: 100,
            render: (version: string, record: LegalDocument) => (
                <Space>
                    <Text strong>{version}</Text>
                    {record.isCurrent && (
                        <Tag color="green" icon={<CheckCircleOutlined />}>
                            Active
                        </Tag>
                    )}
                </Space>
            ),
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Effective Date',
            dataIndex: 'effectiveDate',
            key: 'effectiveDate',
            width: 150,
            render: (date: string) => dayjs(date).format('DD MMM YYYY'),
        },
        {
            title: 'Created',
            key: 'created',
            width: 200,
            render: (_: any, record: LegalDocument) => (
                <Space direction="vertical" size={0}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {dayjs(record.createdAt).format('DD MMM YYYY HH:mm')}
                    </Text>
                    {record.createdBy && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            <UserOutlined style={{ marginRight: 4 }} />
                            {record.createdBy.firstName} {record.createdBy.lastName}
                        </Text>
                    )}
                </Space>
            ),
        },
        {
            title: 'Summary',
            dataIndex: 'summary',
            key: 'summary',
            ellipsis: true,
            render: (summary: string | null) => summary || <Text type="secondary">-</Text>,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_: any, record: LegalDocument) => (
                <Space>
                    <Tooltip title="Preview">
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => openPreviewModal(record)}
                        />
                    </Tooltip>
                    {!record.isCurrent && (
                        <Tooltip title="Activate this version">
                            <Button
                                type="text"
                                icon={<RocketOutlined />}
                                onClick={() => openActivateModal(record)}
                            />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    const renderDocumentTable = (docs: LegalDocument[] | undefined, documentType: string) => {
        const currentDoc = docs?.find(d => d.isCurrent);

        return (
            <div>
                {currentDoc && (
                    <Alert
                        message={`Current Version: ${currentDoc.version}`}
                        description={`"${currentDoc.title}" - Effective since ${dayjs(currentDoc.effectiveDate).format('DD MMM YYYY')}`}
                        type="success"
                        showIcon
                        icon={<CheckCircleOutlined />}
                        style={{ marginBottom: 16 }}
                        action={
                            <Button size="small" onClick={() => openPreviewModal(currentDoc)}>
                                View
                            </Button>
                        }
                    />
                )}

                {!currentDoc && (
                    <Alert
                        message="No Active Document"
                        description="No version of this document is currently active. Create and activate a version to enable legal acceptance."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text type="secondary">
                        <HistoryOutlined style={{ marginRight: 8 }} />
                        {docs?.length || 0} version(s)
                    </Text>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => openCreateModal(documentType)}
                    >
                        Create New Version
                    </Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={docs}
                    rowKey="id"
                    pagination={false}
                    size="small"
                />
            </div>
        );
    };

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <Spin size="large" />
            </div>
        );
    }

    const tabItems = [
        {
            key: 'privacy_policy',
            label: (
                <span>
                    <SafetyOutlined style={{ marginRight: 8 }} />
                    Privacy Policy
                </span>
            ),
            children: renderDocumentTable(documents?.privacyPolicy, 'privacy_policy'),
        },
        {
            key: 'terms_of_service',
            label: (
                <span>
                    <FileTextOutlined style={{ marginRight: 8 }} />
                    Terms of Service
                </span>
            ),
            children: renderDocumentTable(documents?.termsOfService, 'terms_of_service'),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Card>
                <div style={{ marginBottom: 24 }}>
                    <Title level={3} style={{ margin: 0 }}>
                        <FileTextOutlined style={{ marginRight: 12 }} />
                        Legal Documents
                    </Title>
                    <Text type="secondary">
                        Manage Privacy Policy and Terms of Service documents. Users must accept the current version of each document.
                    </Text>
                </div>

                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                />
            </Card>

            {/* Create Document Modal */}
            <Modal
                title={
                    <Space>
                        <PlusOutlined />
                        Create New {documentTypeToCreate === 'privacy_policy' ? 'Privacy Policy' : 'Terms of Service'} Version
                    </Space>
                }
                open={createModalVisible}
                onCancel={() => setCreateModalVisible(false)}
                footer={null}
                width={900}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreateDocument}
                >
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="version"
                                label="Version"
                                rules={[{ required: true, message: 'Please enter version' }]}
                            >
                                <Input placeholder="e.g., 1.0, 2.0, 2.1" />
                            </Form.Item>
                        </Col>
                        <Col span={16}>
                            <Form.Item
                                name="title"
                                label="Title"
                                rules={[{ required: true, message: 'Please enter title' }]}
                            >
                                <Input placeholder="e.g., Privacy Policy" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="effectiveDate"
                                label="Effective Date"
                                rules={[{ required: true, message: 'Please select effective date' }]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="summary"
                                label="Change Summary"
                            >
                                <Input placeholder="Brief description of changes in this version" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="content"
                        label="Content (Markdown supported)"
                        rules={[{ required: true, message: 'Please enter document content' }]}
                    >
                        <TextArea
                            rows={15}
                            placeholder="Enter the full document content in Markdown format..."
                            style={{ fontFamily: 'monospace' }}
                        />
                    </Form.Item>

                    <Alert
                        message="Markdown Preview"
                        description="The content will be rendered as Markdown when displayed to users."
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />

                    <Form.Item
                        name="activateImmediately"
                        valuePropName="checked"
                    >
                        <Checkbox>
                            Activate immediately (make this the current version)
                        </Checkbox>
                    </Form.Item>

                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setCreateModalVisible(false)}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit" loading={isCreating}>
                                Create Document
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Modal>

            {/* Preview Modal */}
            <Modal
                title={
                    <Space>
                        <EyeOutlined />
                        {selectedDocument?.title} - Version {selectedDocument?.version}
                        {selectedDocument?.isCurrent && (
                            <Tag color="green">Active</Tag>
                        )}
                    </Space>
                }
                open={previewModalVisible}
                onCancel={() => {
                    setPreviewModalVisible(false);
                    setSelectedDocument(null);
                }}
                footer={[
                    <Button key="close" onClick={() => {
                        setPreviewModalVisible(false);
                        setSelectedDocument(null);
                    }}>
                        Close
                    </Button>,
                ]}
                width={800}
            >
                {selectedDocument && (
                    <div>
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={12}>
                                <Text type="secondary">Effective Date:</Text>
                                <br />
                                <Text strong>{dayjs(selectedDocument.effectiveDate).format('DD MMM YYYY')}</Text>
                            </Col>
                            <Col span={12}>
                                <Text type="secondary">Created:</Text>
                                <br />
                                <Text strong>{dayjs(selectedDocument.createdAt).format('DD MMM YYYY HH:mm')}</Text>
                            </Col>
                        </Row>

                        {selectedDocument.summary && (
                            <Alert
                                message="Change Summary"
                                description={selectedDocument.summary}
                                type="info"
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        <Divider />

                        <div
                            style={{
                                maxHeight: 500,
                                overflow: 'auto',
                                padding: 16,
                                backgroundColor: '#fafafa',
                                borderRadius: 8,
                                border: '1px solid #d9d9d9',
                            }}
                        >
                            <ReactMarkdown>{selectedDocument.content}</ReactMarkdown>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Activate Modal */}
            <Modal
                title={
                    <Space>
                        <RocketOutlined />
                        Activate Document Version
                    </Space>
                }
                open={activateModalVisible}
                onCancel={() => {
                    setActivateModalVisible(false);
                    setSelectedDocument(null);
                }}
                onOk={handleActivateDocument}
                okText="Activate"
                okButtonProps={{ loading: isActivating }}
                width={500}
            >
                {selectedDocument && (
                    <div>
                        <Alert
                            message="Confirm Activation"
                            description={
                                <>
                                    You are about to activate <strong>{selectedDocument.title} v{selectedDocument.version}</strong>.
                                    This will make it the current version that users must accept.
                                </>
                            }
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />

                        {documentStats && (
                            <Card size="small" style={{ marginBottom: 16 }}>
                                <Statistic
                                    title="Users who accepted this version"
                                    value={documentStats.totalAcceptances}
                                    prefix={<UserOutlined />}
                                />
                            </Card>
                        )}

                        <Paragraph type="secondary">
                            After activation, users who have not accepted this version will be prompted to review and accept it on their next login.
                        </Paragraph>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default AdminLegalDocumentsPage;
